<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <h4 class="mb-50">
          {{ $t('ledger_item_title') }}
        </h4>
        <b-button
          v-if="hasPermission('create items')"
          variant="primary"
          size="sm"
          class="rounded-pill"
          :to="{ name: 'ledger-items-new' }"
        >
          {{ $t('ledger_item_new') }}
        </b-button>
      </div>

      <b-row class="mb-1">
        <b-col
          cols="12"
          md="5"
        >
          <b-form-input
            v-model="keyword"
            :placeholder="$t('ledger_item_search_ph')"
            @keyup.enter="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-select
            v-model="kind"
            :options="kindOptions"
            @change="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-button
            variant="outline-primary"
            class="rounded-pill"
            @click="reload"
          >
            {{ $t('ledger_search') }}
          </b-button>
        </b-col>
      </b-row>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_item_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            :tbody-tr-class="rowClass"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(code)="data">
              <b-link :to="showRoute(data.item)">
                {{ data.item.code }}
              </b-link>
            </template>
            <template #cell(kind)="data">
              {{ kindLabel(data.item.kind) }}
            </template>
            <template #cell(sale_price)="data">
              <span class="ledger-num">{{ money(data.item.sale_price) }}</span>
            </template>
            <template #cell(qty_on_hand)="data">
              <span class="ledger-num">{{ qty(data.item.qty_on_hand) }}</span>
            </template>
            <template #cell(in_use)="data">
              {{ data.item.in_use ? $t('ledger_yes') : $t('ledger_no') }}
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-item-card"
            :class="{ 'ledger-inactive': item.in_use === false }"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ item.code }}</strong>
              <span>{{ kindLabel(item.kind) }}</span>
            </div>
            <div>{{ item.name }}</div>
            <div class="small text-muted">
              {{ item.unit || '—' }}
            </div>
            <div class="d-flex justify-content-between ledger-num">
              <span>{{ money(item.sale_price) }}</span>
              <span>{{ qty(item.qty_on_hand) }}</span>
            </div>
          </article>
        </div>
        <b-pagination
          v-if="meta.total"
          v-model="page"
          class="mt-1"
          align="center"
          :total-rows="meta.total"
          :per-page="meta.per_page || 25"
          @change="onPage"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BTable, BLink, BSpinner, BPagination,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getItems } from '@/api/accounting/items'
import {
  listFrom, formatLedgerMoney, formatLedgerQty, apiErrorMessage,
} from '@/utils/ledger'

function kindLabel(kind) {
  if (kind && typeof kind === 'object') return kind.label_ar || kind.label || kind.value || ''
  return kind ? String(kind) : ''
}

export default {
  name: 'ItemList',
  components: {
    BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BTable, BLink, BSpinner, BPagination,
  },
  data() {
    return {
      loading: false,
      rows: [],
      meta: {},
      keyword: '',
      kind: '',
      page: 1,
    }
  },
  computed: {
    kindOptions() {
      return [
        { value: '', text: this.$t('ledger_item_kind_all') },
        { value: 'service', text: this.$t('ledger_item_kind_service') },
        { value: 'stock', text: this.$t('ledger_item_kind_stock') },
      ]
    },
    fields() {
      return [
        { key: 'code', label: this.$t('ledger_item_code') },
        { key: 'name', label: this.$t('ledger_item_name') },
        { key: 'kind', label: this.$t('ledger_item_kind') },
        { key: 'unit', label: this.$t('ledger_item_unit') },
        { key: 'sale_price', label: this.$t('ledger_item_sale_price') },
        { key: 'qty_on_hand', label: this.$t('ledger_item_qty_on_hand') },
        { key: 'in_use', label: this.$t('ledger_item_in_use') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    kindLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    qty(value) {
      return formatLedgerQty(value || 0, this.$i18n.locale)
    },
    showRoute(item) {
      return { name: 'ledger-items-view', params: { id: item.id } }
    },
    rowClass(item) {
      return item && item.in_use === false ? 'ledger-inactive' : ''
    },
    reload() {
      this.page = 1
      this.load()
    },
    onPage(page) {
      this.page = page
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = { page: this.page, per_page: 25 }
        const keyword = String(this.keyword || '').trim()
        if (keyword) params.keyword = keyword
        if (this.kind) params.kind = this.kind
        const res = await getItems(params)
        this.rows = listFrom(res)
        this.meta = res.meta || {}
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: error?.response?.status === 422 ? 'warning' : 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ledger-item-card { border-bottom: 1px solid #eee; padding: 0.85rem 0.15rem; cursor: pointer; }
.ledger-inactive,
::v-deep .ledger-inactive { opacity: 0.55; }
</style>
